/**
 * KursSzkolenia — pełnoekranowy kurs Akademii, wspólny dla DWÓCH wejść.
 * ==============================================================================
 *
 * Do 13.09.2026 kurs (program → slajdy → lektor → czat trenera → postęp) żył
 * w całości w `pages/partner/AkademiaPage.tsx` i był przypięty do partnera.
 * Użytkownik najemcy po Genesis Jan-Dar nie miał gdzie wrócić do
 * prezentacji modułów ani zapisać, że przeszedł rozdział o Fabrice. Plan
 * GENESIS_JEDNA_PRAWDA_PLAN.md §4.7: jeden program (`PROGRAM_SZKOLENIA`
 * z polem `odbiorca`), dwa cienkie wejścia — `/partner/akademia` (partner,
 * `partnerOrgId`) i `/admin/akademia` (użytkownik, `currentOrganization`).
 *
 * Ten komponent nie wie, KIM jest kursant — dostaje `orgId`, `userId`
 * i `odbiorca`, a różnice sprowadzają się do zapytania (`?odbiorca=`), ciała
 * ukończenia i opcjonalnych wstawek „dla Was" przy rozdziałach organowych
 * (`genesis_narracja_modulow`, 692) — te przekazuje strona użytkownika.
 *
 * DWA TORY, JEDEN EKRAN (plan §7 / E4, 13.09.2026): rozmowę z nexus-core
 * prowadzi `TransportKursu` (`transportKursuCore.ts`). Domyślnie tor powłoki
 * (JWT Supabase, `transportPowloki`); Nexus Mobile Companion podaje
 * `transportMobilny` z tokenem sesji PIN, a wstawki „dla Was" dostaje wtedy
 * razem z programem (klucz `anon` nie przeczyta tabeli z RLS członków).
 * Komponent nie wie, którym torem jedzie — i tak ma być.
 *
 * Szkolenie prowadzi GŁOS: bramka audio (`BramkaAudio`) przed kursem, lektor
 * (`useLektor`) czyta każdy slajd. Decyzja bramki jest wspólna z Genesis
 * (jeden klucz sessionStorage) — kto sprawdził dźwięk w Kinie, nie sprawdza
 * go drugi raz tutaj.
 */

import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Loader2,
  MessageCircle,
  RefreshCw,
  Send,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BramkaAudio } from '../audio/BramkaAudio';
import { tekstLektora, zapamietajBramke, zapamietanaBramka, zlozZdania } from '../audio/lektorCore';
import { useLektor } from '../audio/useLektor';

import { transportPowloki } from './transportKursu';
import type {
  OdbiorcaSzkolenia,
  RozdzialInfo,
  Slajd,
  TransportKursu,
  WstawkaOrganu,
} from './transportKursuCore';

export type {
  OdbiorcaSzkolenia,
  RozdzialInfo,
  Slajd,
  SzkolenieOdpowiedz,
  TransportKursu,
  WstawkaOrganu,
} from './transportKursuCore';

interface WiadomoscCzatu {
  rola: 'ja' | 'nexus';
  tekst: string;
}

export interface KursSzkoleniaProps {
  /** Najemca kursu; `null` = kontekst jeszcze się ładuje albo go nie ma. */
  orgId: string | null;
  ladujeKontekst: boolean;
  userId: string | undefined;
  odbiorca: OdbiorcaSzkolenia;
  tytul: string;
  podtytul: string;
  /** Dokąd prowadzi „Zamknij". */
  wyjscie: string;
  /**
   * Wstawki „dla Was" i znaczenia nazw per klucz rozdziału organowego.
   * Gdy brak — bierze te, które przyszły z programem (tor mobilny).
   */
  wstawkiOrganow?: Record<string, WstawkaOrganu>;
  /** Tor rozmowy z nexus-core; domyślnie powłoka (JWT) dla `orgId`. */
  transport?: TransportKursu;
}

/** Trener otwarty od wejścia tylko tam, gdzie mieści się obok slajdu. */
function czatMiesciSieObok(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(min-width: 1024px)').matches
    : true;
}

/** Generacja rozdziału potrafi trwać — dajemy modelowi dwie minuty. */
const ROZDZIAL_TIMEOUT_MS = 120_000;

export function KursSzkolenia({
  orgId,
  ladujeKontekst,
  userId,
  odbiorca,
  tytul,
  podtytul,
  wyjscie,
  wstawkiOrganow,
  transport,
}: KursSzkoleniaProps) {
  const navigate = useNavigate();

  const tor = useMemo<TransportKursu | null>(
    () => transport ?? (orgId ? transportPowloki({ orgId, odbiorca, userId }) : null),
    [transport, orgId, odbiorca, userId]
  );

  const [rozdzialy, setRozdzialy] = useState<RozdzialInfo[]>([]);
  const [postep, setPostep] = useState({ ukonczone: 0, wszystkie: 0 });
  const [aktywny, setAktywny] = useState<string | null>(null);
  const [slajdy, setSlajdy] = useState<Slajd[]>([]);
  const [numerSlajdu, setNumerSlajdu] = useState(0);
  const [laduje, setLaduje] = useState(true);
  const [ladujeRozdzial, setLadujeRozdzial] = useState(false);
  const [odhaczam, setOdhaczam] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  // Błąd TREŚCI rozdziału mieszka osobno od banera: ten dotyczy jednego
  // rozdziału i ma własne „Spróbuj ponownie" w miejscu slajdu. Do 01.10.2026
  // szedł do banera, a w miejscu slajdu stało „Wybierz rozdział z programu."
  // przy rozdziale, który był wybrany — i baner nie znikał po zmianie rozdziału.
  const [bladRozdzialu, setBladRozdzialu] = useState<string | null>(null);
  const [probaRozdzialu, setProbaRozdzialu] = useState(0);
  const [wstawkiZProgramu, setWstawkiZProgramu] = useState<Record<string, WstawkaOrganu>>({});

  // -- Bramka audio + lektor --------------------------------------------------
  const [bramka, setBramka] = useState(zapamietanaBramka);
  const {
    mow,
    zatrzymaj,
    mowi: lektorMowi,
  } = useLektor({ wlaczony: bramka.lektor, tenantId: orgId });

  const [czatOtwarty, setCzatOtwarty] = useState(czatMiesciSieObok);
  const [wiadomosci, setWiadomosci] = useState<WiadomoscCzatu[]>([
    {
      rola: 'nexus',
      tekst:
        'Cześć! Prowadzę Cię przez szkolenie produktowe INSOLITUM Universe. Pytaj o wszystko w trakcie — jestem obok.',
    },
  ]);
  const [pytanie, setPytanie] = useState('');
  const [nexusPisze, setNexusPisze] = useState(false);
  const conversationRef = useRef<string | null>(null);
  const koniecCzatuRef = useRef<HTMLDivElement>(null);

  const zaladujProgram = useCallback(async () => {
    if (!orgId || !tor) return;
    setLaduje(true);
    setBlad(null);
    try {
      const dane = await tor.program();
      setRozdzialy(dane.rozdzialy);
      setPostep({ ukonczone: dane.ukonczone, wszystkie: dane.wszystkie });
      setWstawkiZProgramu(dane.wstawki ?? {});
      // Wejście ląduje na pierwszym NIEukończonym rozdziale — tam, gdzie praca.
      const pierwszyOtwarty = dane.rozdzialy.find((r) => !r.ukonczony) ?? dane.rozdzialy[0];
      if (pierwszyOtwarty) setAktywny((obecny) => obecny ?? pierwszyOtwarty.klucz);
    } catch (err) {
      setBlad(err instanceof Error ? err.message : 'Nie udało się pobrać programu szkolenia');
    } finally {
      setLaduje(false);
    }
  }, [orgId, tor]);

  useEffect(() => {
    void zaladujProgram();
  }, [zaladujProgram]);

  // Treść rozdziału dopiero PO bramce — generacja slajdów kosztuje przebieg
  // modelu, a kursant przed testem rozmowy jeszcze nie zaczął kursu.
  useEffect(() => {
    if (!orgId || !tor || !aktywny || !bramka.zaliczona) return;
    let aktualne = true;
    setLadujeRozdzial(true);
    setSlajdy([]);
    setNumerSlajdu(0);
    setBladRozdzialu(null);
    // Baner (np. nieudany zapis postępu) dotyczył poprzedniego kroku — nowy
    // rozdział zaczyna się bez niego.
    setBlad(null);
    tor
      .rozdzial(aktywny, { timeoutMs: ROZDZIAL_TIMEOUT_MS })
      .then((dane) => {
        if (aktualne) setSlajdy(dane.slajdy);
      })
      .catch((err) => {
        if (aktualne)
          setBladRozdzialu(
            err instanceof Error ? err.message : 'Nie udało się przygotować rozdziału'
          );
      })
      .finally(() => {
        if (aktualne) setLadujeRozdzial(false);
      });
    return () => {
      aktualne = false;
    };
  }, [orgId, tor, aktywny, bramka.zaliczona, probaRozdzialu]);

  useEffect(() => {
    koniecCzatuRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [wiadomosci, nexusPisze]);

  // -- Lektor: Nexus czyta bieżący slajd (na pierwszym — także wstawkę) -------
  const slajd = slajdy[numerSlajdu] ?? null;
  const wstawki = wstawkiOrganow ?? wstawkiZProgramu;
  const wstawka = aktywny ? wstawki[aktywny] : undefined;
  useEffect(() => {
    if (!bramka.zaliczona || !bramka.lektor || !slajd || !aktywny) return;
    const tekst =
      numerSlajdu === 0 && wstawka?.dlaWas
        ? zlozZdania([tekstLektora(slajd), `Co to znaczy dla Was: ${wstawka.dlaWas}`])
        : tekstLektora(slajd);
    if (!tekst) return;
    // Lektor jest dodatkiem — slajd stoi na ekranie, cisza nie blokuje kursu.
    void mow(`${odbiorca}:${aktywny}:${numerSlajdu}`, tekst);
    return () => zatrzymaj();
  }, [
    bramka.zaliczona,
    bramka.lektor,
    slajd,
    aktywny,
    numerSlajdu,
    wstawka,
    odbiorca,
    mow,
    zatrzymaj,
  ]);

  const przelaczLektora = useCallback(() => {
    setBramka((b) => {
      if (b.lektor) zatrzymaj();
      zapamietajBramke(b.lektor ? 'cisza' : 'glos');
      return { ...b, lektor: !b.lektor };
    });
  }, [zatrzymaj]);

  const ukonczRozdzial = async () => {
    if (!orgId || !tor || !aktywny) return;
    setOdhaczam(true);
    try {
      const dane = await tor.ukoncz(aktywny);
      setPostep({ ukonczone: dane.ukonczone, wszystkie: dane.wszystkie });
      setRozdzialy((stare) =>
        stare.map((r) => (r.klucz === aktywny ? { ...r, ukonczony: true } : r))
      );
      const indeks = rozdzialy.findIndex((r) => r.klucz === aktywny);
      const nastepny = rozdzialy.slice(indeks + 1).find((r) => !r.ukonczony);
      if (nastepny) setAktywny(nastepny.klucz);
    } catch (err) {
      setBlad(err instanceof Error ? err.message : 'Nie udało się zapisać postępu');
    } finally {
      setOdhaczam(false);
    }
  };

  const zapytajNexusa = async () => {
    const tekst = pytanie.trim();
    if (!tekst || !tor?.czat || nexusPisze) return;
    setPytanie('');
    setWiadomosci((w) => [...w, { rola: 'ja', tekst }]);
    setNexusPisze(true);
    try {
      const dane = await tor.czat(tekst, conversationRef.current ?? undefined);
      if (dane.conversationId) conversationRef.current = dane.conversationId;
      setWiadomosci((w) => [...w, { rola: 'nexus', tekst: dane.odpowiedz }]);
    } catch {
      setWiadomosci((w) => [
        ...w,
        { rola: 'nexus', tekst: 'Przepraszam, coś mi przerwało — zapytaj proszę jeszcze raz.' },
      ]);
    } finally {
      setNexusPisze(false);
    }
  };

  const aktywnyRozdzial = rozdzialy.find((r) => r.klucz === aktywny) ?? null;
  const ostatniSlajd = slajdy.length > 0 && numerSlajdu === slajdy.length - 1;
  const wszystkoUkonczone = postep.wszystkie > 0 && postep.ukonczone >= postep.wszystkie;
  const procent =
    postep.wszystkie > 0 ? Math.round((postep.ukonczone / postep.wszystkie) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-aurora-bg text-aurora-text">
      {/* Nagłówek: tytuł, postęp, lektor, wyjście */}
      <header className="flex items-center gap-4 px-5 py-3 border-b border-aurora-border shrink-0">
        <GraduationCap className="w-6 h-6 text-aurora-brand shrink-0" />
        <div className="min-w-0">
          <h1 className="font-semibold leading-tight truncate">{tytul}</h1>
          <p className="text-xs text-aurora-muted truncate">{podtytul}</p>
        </div>
        <div className="ml-auto flex items-center gap-3 shrink-0">
          <div className="hidden sm:block w-40 h-2 rounded-full bg-aurora-border overflow-hidden">
            <div
              className="h-full bg-aurora-brand transition-all"
              style={{ width: `${procent}%` }}
            />
          </div>
          <span className="mono text-sm text-aurora-muted">
            {postep.ukonczone}/{postep.wszystkie}
          </span>
          {bramka.zaliczona && (
            <button
              onClick={przelaczLektora}
              className="btn btn--ghost"
              title={bramka.lektor ? 'Wycisz lektora' : 'Włącz lektora'}
            >
              {bramka.lektor ? (
                <Volume2
                  className={`w-4 h-4 ${lektorMowi ? 'text-aurora-brand animate-pulse' : ''}`}
                />
              ) : (
                <VolumeX className="w-4 h-4" />
              )}
            </button>
          )}
          {tor?.czat && bramka.zaliczona && (
            <button
              onClick={() => setCzatOtwarty((o) => !o)}
              className="btn btn--ghost"
              title="Rozmowa z Nexusem"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => {
              zatrzymaj();
              navigate(wyjscie);
            }}
            className="btn btn--ghost"
            title="Zamknij"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </header>

      {blad && (
        <div className="mx-5 mt-3 bg-aurora-danger/10 border border-aurora-danger/30 rounded-ids-md p-3 text-aurora-danger text-sm">
          {blad}
        </div>
      )}

      {!bramka.zaliczona ? (
        /* ── Bramka audio: głośniki + mikrofon + test rozmowy ── */
        <div className="flex-1 flex items-center justify-center p-6">
          <BramkaAudio
            tenantId={orgId}
            opis="Nexus poprowadzi Cię przez szkolenie głosem."
            onGotowe={(lektor) => setBramka({ zaliczona: true, lektor })}
          />
        </div>
      ) : (
        <div className="flex flex-1 min-h-0">
          {/* Program — lista rozdziałów */}
          <nav className="w-64 shrink-0 border-r border-aurora-border overflow-y-auto p-3 hidden md:block">
            <p className="text-xs uppercase tracking-wide text-aurora-muted px-2 mb-2">Program</p>
            {rozdzialy.map((r, i) => (
              <button
                key={r.klucz}
                onClick={() => setAktywny(r.klucz)}
                className={`w-full text-left px-3 py-2 rounded-ids-md mb-1 text-sm flex items-start gap-2 transition-colors ${
                  r.klucz === aktywny
                    ? 'bg-aurora-brand/15 text-aurora-text'
                    : 'text-aurora-muted hover:bg-aurora-border/40'
                }`}
              >
                <span className="mono text-xs mt-0.5 shrink-0 w-5">
                  {r.ukonczony ? <Check className="w-4 h-4 text-aurora-success" /> : `${i + 1}.`}
                </span>
                <span>{r.tytul}</span>
              </button>
            ))}
          </nav>

          {/* Prezentacja */}
          <main className="flex-1 min-w-0 flex flex-col">
            {/* Program na wąskim ekranie — lista rozdziałów jako wybór, nie boczny panel */}
            {rozdzialy.length > 0 && (
              <label className="md:hidden px-4 pt-3 block">
                <span className="sr-only">Rozdział</span>
                <select
                  value={aktywny ?? ''}
                  onChange={(e) => setAktywny(e.target.value)}
                  className="input w-full"
                  aria-label="Rozdział szkolenia"
                >
                  {rozdzialy.map((r, i) => (
                    <option key={r.klucz} value={r.klucz}>
                      {r.ukonczony ? '✓ ' : `${i + 1}. `}
                      {r.tytul}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {laduje || ladujeKontekst ? (
              <div className="flex-1 flex items-center justify-center text-aurora-muted">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Wczytuję program szkolenia…
              </div>
            ) : wszystkoUkonczone && !aktywnyRozdzial ? null : ladujeRozdzial ? (
              <div className="flex-1 flex flex-col items-center justify-center text-aurora-muted gap-3">
                <Loader2 className="w-8 h-8 animate-spin" />
                <p>Nexus przygotowuje rozdział „{aktywnyRozdzial?.tytul}”…</p>
                <p className="text-xs">Pierwsze otwarcie rozdziału może potrwać do minuty.</p>
              </div>
            ) : slajd ? (
              <>
                <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center gap-4 p-6">
                  {numerSlajdu === 0 && (wstawka?.znaczenieNazwy || wstawka?.dlaWas) && (
                    <div className="card w-full max-w-3xl p-5 md:p-6 space-y-3">
                      {wstawka.znaczenieNazwy && (
                        <div>
                          <p className="text-xs uppercase tracking-wide text-aurora-brand mb-1">
                            Skąd ta nazwa
                          </p>
                          <p className="text-sm text-aurora-muted">{wstawka.znaczenieNazwy}</p>
                        </div>
                      )}
                      {wstawka.dlaWas && (
                        <div>
                          <p className="text-xs uppercase tracking-wide text-aurora-brand mb-1">
                            Co to znaczy dla Was
                          </p>
                          <p className="text-sm">{wstawka.dlaWas}</p>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="card w-full max-w-3xl p-8 md:p-12">
                    <p className="text-xs uppercase tracking-wide text-aurora-muted mb-2">
                      {aktywnyRozdzial?.tytul} · slajd {numerSlajdu + 1} z {slajdy.length}
                    </p>
                    <h2 className="text-2xl md:text-3xl font-semibold mb-6">{slajd.tytul}</h2>
                    <ul className="space-y-3">
                      {slajd.punkty.map((p, i) => (
                        <li key={i} className="flex items-start gap-3 text-base md:text-lg">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-aurora-brand shrink-0" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <footer className="flex items-center justify-center gap-3 p-4 border-t border-aurora-border shrink-0">
                  <button
                    onClick={() => setNumerSlajdu((n) => Math.max(0, n - 1))}
                    disabled={numerSlajdu === 0}
                    className="btn btn--ghost"
                  >
                    <ChevronLeft className="w-4 h-4" /> Poprzedni
                  </button>
                  <span className="mono text-xs text-aurora-muted">
                    {numerSlajdu + 1} / {slajdy.length}
                  </span>
                  {ostatniSlajd ? (
                    aktywnyRozdzial?.ukonczony ? (
                      <span className="text-sm text-aurora-success flex items-center gap-1">
                        <Check className="w-4 h-4" /> Rozdział ukończony
                      </span>
                    ) : (
                      <button
                        onClick={ukonczRozdzial}
                        disabled={odhaczam}
                        className="btn btn--primary"
                      >
                        {odhaczam ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}{' '}
                        Ukończ rozdział
                      </button>
                    )
                  ) : (
                    <button
                      onClick={() => setNumerSlajdu((n) => Math.min(slajdy.length - 1, n + 1))}
                      className="btn btn--primary"
                    >
                      Dalej <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </footer>
              </>
            ) : bladRozdzialu && aktywnyRozdzial ? (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="card w-full max-w-xl p-6 md:p-8 space-y-4 text-center" role="alert">
                  <AlertTriangle className="w-8 h-8 text-aurora-danger mx-auto" />
                  <h2 className="text-lg font-semibold">
                    Rozdział „{aktywnyRozdzial.tytul}” nie jest jeszcze gotowy
                  </h2>
                  <p className="text-sm text-aurora-muted">{bladRozdzialu}</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                    <button
                      onClick={() => setProbaRozdzialu((p) => p + 1)}
                      className="btn btn--primary"
                    >
                      <RefreshCw className="w-4 h-4" /> Spróbuj ponownie
                    </button>
                    <span className="text-xs text-aurora-muted">
                      albo wybierz inny rozdział z programu
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-aurora-muted">
                Wybierz rozdział z programu.
              </div>
            )}

            {wszystkoUkonczone && (
              <div className="p-4 border-t border-aurora-border text-center text-sm text-aurora-success shrink-0">
                {odbiorca === 'partner'
                  ? 'Szkolenie ukończone — egzaminy wiedzy są odblokowane. Napisz Nexusowi „przepytaj mnie”, kiedy zechcesz.'
                  : 'Szkolenie ukończone — znasz już cały Universe. Nexus jest obok, gdy zechcesz coś powtórzyć.'}
              </div>
            )}
          </main>

          {/* Rozmowa z Nexusem w trakcie szkolenia */}
          {czatOtwarty && tor?.czat && (
            <aside className="flex flex-col bg-aurora-bg fixed inset-0 z-20 lg:static lg:w-80 lg:shrink-0 lg:border-l lg:border-aurora-border">
              <div className="flex items-center justify-between px-4 py-3 border-b border-aurora-border">
                <p className="text-xs uppercase tracking-wide text-aurora-muted">Zapytaj Nexusa</p>
                {/* Na wąskim ekranie trener zasłania slajd — musi mieć własne wyjście. */}
                <button
                  onClick={() => setCzatOtwarty(false)}
                  className="btn btn--ghost lg:hidden"
                  title="Wróć do slajdu"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {wiadomosci.map((w, i) => (
                  <div
                    key={i}
                    className={`rounded-ids-md p-3 text-sm whitespace-pre-wrap ${
                      w.rola === 'ja'
                        ? 'bg-aurora-brand/15 ml-6'
                        : 'bg-aurora-border/30 mr-6 text-aurora-text'
                    }`}
                  >
                    {w.tekst}
                  </div>
                ))}
                {nexusPisze && (
                  <div className="rounded-ids-md p-3 text-sm bg-aurora-border/30 mr-6 text-aurora-muted flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Nexus pisze…
                  </div>
                )}
                <div ref={koniecCzatuRef} />
              </div>
              <div className="p-3 border-t border-aurora-border flex gap-2">
                <input
                  value={pytanie}
                  onChange={(e) => setPytanie(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void zapytajNexusa();
                    }
                  }}
                  placeholder="Zadaj pytanie o platformę…"
                  className="input flex-1"
                />
                <button
                  onClick={() => void zapytajNexusa()}
                  disabled={nexusPisze || !pytanie.trim()}
                  className="btn btn--primary"
                  title="Wyślij"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </aside>
          )}
        </div>
      )}
    </div>
  );
}

export default KursSzkolenia;
